import React, { useState, useEffect, useCallback } from "react";
import {
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  IconThumbUp,
  IconThumbDown,
  IconX,
  IconChartPie,
  IconChevronDown,
} from "@tabler/icons-react";
import moment from "moment";
import { UserContext } from "../../../contexts/userContext";
import { useContext } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  getFirestore,
  Timestamp,
  collectionGroup,
} from "firebase/firestore";
import UserDb from "../../../db/userDb";

// Time period options
const TIME_PERIODS = ["Last 30 days"];

// Base model identifier
const BASE_MODEL_PREFIX = "black-forest-labs";

interface FeedbackReason {
  reason: string;
  count: number;
  generations: Array<{
    prompt: string;
    createdAt: Date;
    userId: string;
    generationId: string;
  }>;
}

interface ConversionFunnelData {
  totalSignups: number;
  trainingUsers: number; // Users who created at least 1 training
  modelUsers: number; // Users who created custom models
  generationUsers: number; // Users who generated with custom models
  subscribedUsers: number; // Users with active subscriptions
}

interface UserEngagementData {
  powerUsers: number; // 10+ generations
  regularUsers: number; // 3-9 generations
  casualUsers: number; // 1-2 generations
  inactiveUsers: number; // 0 generations
}

interface TimeToActionData {
  avgTimeToFirstTraining: number; // in days
  avgTimeToCustomModel: number; // in days
  avgTimeToFirstGeneration: number; // in days
  avgTimeToSubscription: number; // in days
}

const HqScreen: React.FC = () => {
  const user = useContext(UserContext);
  const db = getFirestore();

  // Basic metrics state
  const [userCount, setUserCount] = useState<number | null>(null);
  const [generationCount, setGenerationCount] = useState<number | null>(null);
  const [feedbackStats, setFeedbackStats] = useState<{
    positive: number;
    negative: number;
  } | null>(null);
  const [modelCount, setModelCount] = useState<number | null>(null);
  const [trainingCount, setTrainingCount] = useState<number | null>(null);
  const [topReasons, setTopReasons] = useState<FeedbackReason[]>([]);
  const [modelUsage, setModelUsage] = useState<{
    baseModel: number;
    customModels: number;
  }>({ baseModel: 0, customModels: 0 });
  const [userModelUsage, setUserModelUsage] = useState<{
    baseModelOnly: number;
    customModelsOnly: number;
    bothModels: number;
    inactive: number;
  }>({ baseModelOnly: 0, customModelsOnly: 0, bothModels: 0, inactive: 0 });

  // New conversion funnel metrics
  const [conversionFunnel, setConversionFunnel] =
    useState<ConversionFunnelData>({
      totalSignups: 0,
      trainingUsers: 0,
      modelUsers: 0,
      generationUsers: 0,
      subscribedUsers: 0,
    });
  const [userEngagement, setUserEngagement] = useState<UserEngagementData>({
    powerUsers: 0,
    regularUsers: 0,
    casualUsers: 0,
    inactiveUsers: 0,
  });
  const [timeToAction, setTimeToAction] = useState<TimeToActionData>({
    avgTimeToFirstTraining: 0,
    avgTimeToCustomModel: 0,
    avgTimeToFirstGeneration: 0,
    avgTimeToSubscription: 0,
  });

  // UI states
  const [loading, setLoading] = useState(true);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(0); // 0 = Last 7 days, 1 = Last 30 days

  // Get date range based on selected time period
  const getDateRange = useCallback(() => {
    const endDate = moment();
    const startDate = moment().subtract(
      selectedTimePeriod === 0 ? 7 : 30,
      "days"
    );
    return {
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
    };
  }, [selectedTimePeriod]);

  // Fetch metrics from Firestore
  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);

      // Count total users (not filtered by time period)
      const userDb = new UserDb();
      const usersQuery = query(userDb.collection({ userId: "" }));
      const usersSnapshot = await getDocs(usersQuery);
      const totalUsers = usersSnapshot.size;
      setUserCount(totalUsers);

      // Get all users with their creation dates and subscription status
      const usersWithMetadata = new Map<
        string,
        {
          createdAt: Date;
          hasSubscription: boolean;
          firstTrainingAt?: Date;
          firstGenerationAt?: Date;
          firstCustomModelAt?: Date;
          subscriptionDate?: Date;
          generationCount: number;
        }
      >();

      usersSnapshot.forEach((doc) => {
        const data = doc.data() as any;
        usersWithMetadata.set(doc.id, {
          createdAt: data.createdAt?.toDate() || new Date(),
          hasSubscription: false, // Will be updated when checking subscriptions
          firstTrainingAt: undefined,
          subscriptionDate: undefined, // Will be updated when checking subscriptions
          generationCount: 0,
        });
      });

      // Check subscriptions for each user
      const subscriptionPromises = Array.from(usersWithMetadata.keys()).map(
        async (userId) => {
          try {
            const subscriptionsQuery = query(
              collection(db, `users/${userId}/subscriptions`),
              where("status", "!=", "canceled")
            );
            const subscriptionsSnapshot = await getDocs(subscriptionsQuery);

            if (!subscriptionsSnapshot.empty) {
              const userMeta = usersWithMetadata.get(userId);
              if (userMeta) {
                userMeta.hasSubscription = true;
                // Get the earliest subscription date
                subscriptionsSnapshot.docs.forEach((subDoc) => {
                  const subData = subDoc.data();
                  const subDate =
                    subData.created?.toDate() || subData.createdAt?.toDate();
                  if (
                    subDate &&
                    (!userMeta.subscriptionDate ||
                      subDate < userMeta.subscriptionDate)
                  ) {
                    userMeta.subscriptionDate = subDate;
                  }
                });
              }
            }
          } catch (error) {
            console.warn(
              `Failed to check subscriptions for user ${userId}:`,
              error
            );
          }
        }
      );

      // Wait for all subscription checks to complete
      await Promise.all(subscriptionPromises);

      // Count models created within the time period
      const modelsQuery = collectionGroup(db, "models");
      const modelsSnapshot = await getDocs(modelsQuery);

      // Filter models by date range and track custom model users
      let filteredModelCount = 0;
      const customModelUsers = new Set<string>();

      modelsSnapshot.forEach((doc) => {
        const data = doc.data();
        const modelDate = data.createdAt?.toDate();
        const userId = doc.ref.path.split("/")[1];

        if (modelDate && modelDate >= startDate && modelDate <= endDate) {
          filteredModelCount++;
        }

        // Track users who created custom models (ever, not just in period)
        if (modelDate) {
          customModelUsers.add(userId);
          const userMeta = usersWithMetadata.get(userId);
          if (
            userMeta &&
            (!userMeta.firstCustomModelAt ||
              modelDate < userMeta.firstCustomModelAt)
          ) {
            userMeta.firstCustomModelAt = modelDate;
          }
        }
      });
      setModelCount(filteredModelCount);

      // Count trainings created within the time period and track training users
      const trainingsQuery = collectionGroup(db, "trainings");
      const trainingsSnapshot = await getDocs(trainingsQuery);

      // Filter trainings by date range and track training users
      let filteredTrainingCount = 0;
      const trainingUsers = new Set<string>();

      trainingsSnapshot.forEach((doc) => {
        const data = doc.data();
        const trainingDate = data.createdAt?.toDate();
        const userId = doc.ref.path.split("/")[1];

        if (
          trainingDate &&
          trainingDate >= startDate &&
          trainingDate <= endDate
        ) {
          filteredTrainingCount++;
        }

        // Track users who created trainings (ever, not just in period)
        if (trainingDate) {
          trainingUsers.add(userId);
          const userMeta = usersWithMetadata.get(userId);
          if (
            userMeta &&
            (!userMeta.firstTrainingAt ||
              trainingDate < userMeta.firstTrainingAt)
          ) {
            userMeta.firstTrainingAt = trainingDate;
          }
        }
      });
      setTrainingCount(filteredTrainingCount);

      // Count generations and feedback using collectionGroup
      const generationsQuery = collectionGroup(db, "generations");
      const generationsSnapshot = await getDocs(generationsQuery);

      // Filter by date range and count
      let filteredGenerationsCount = 0;
      let positiveCount = 0;
      let negativeCount = 0;
      let baseModelCount = 0;
      let customModelCount = 0;
      const reasonsMap = new Map<
        string,
        {
          count: number;
          generations: Array<{
            prompt: string;
            createdAt: Date;
            userId: string;
            generationId: string;
          }>;
        }
      >();

      // Track user model usage and generation patterns
      const userModelMap = new Map<
        string,
        {
          usedBaseModel: boolean;
          usedCustomModel: boolean;
        }
      >();

      const userGenerationCounts = new Map<string, number>();
      const customModelGenerationUsers = new Set<string>(); // Users who generated with custom models

      generationsSnapshot.forEach((doc) => {
        const data = doc.data();
        const generationDate = data.createdAt?.toDate();
        const userId = doc.ref.path.split("/")[1];

        // Track all-time generation counts for engagement metrics
        const currentCount = userGenerationCounts.get(userId) || 0;
        userGenerationCounts.set(userId, currentCount + 1);

        // Track first generation date for time-to-action metrics (only for custom models)
        if (!data.modelIdentifier.startsWith(BASE_MODEL_PREFIX)) {
          customModelGenerationUsers.add(userId);
          const userMeta = usersWithMetadata.get(userId);
          if (
            userMeta &&
            generationDate &&
            (!userMeta.firstGenerationAt ||
              generationDate < userMeta.firstGenerationAt)
          ) {
            userMeta.firstGenerationAt = generationDate;
          }
        }

        if (
          generationDate &&
          generationDate >= startDate &&
          generationDate <= endDate
        ) {
          filteredGenerationsCount++;

          // Track base model vs custom model usage
          if (data.modelIdentifier.startsWith(BASE_MODEL_PREFIX)) {
            baseModelCount++;
            // Track per user
            const userStats = userModelMap.get(userId) || {
              usedBaseModel: false,
              usedCustomModel: false,
            };
            userStats.usedBaseModel = true;
            userModelMap.set(userId, userStats);
          } else {
            customModelCount++;
            // Track per user
            const userStats = userModelMap.get(userId) || {
              usedBaseModel: false,
              usedCustomModel: false,
            };
            userStats.usedCustomModel = true;
            userModelMap.set(userId, userStats);
          }

          if (data.feedback) {
            if (data.feedback.isPositive === true) {
              positiveCount++;
            } else if (data.feedback.isPositive === false) {
              negativeCount++;

              // Track reasons for negative feedback with generation data
              if (data.feedback.reason) {
                const reason = data.feedback.reason;
                const existingReason = reasonsMap.get(reason);

                if (existingReason) {
                  existingReason.count += 1;
                  existingReason.generations.push({
                    prompt: data.prompt || "No prompt available",
                    createdAt: generationDate,
                    userId: userId,
                    generationId: doc.id,
                  });
                } else {
                  reasonsMap.set(reason, {
                    count: 1,
                    generations: [
                      {
                        prompt: data.prompt || "No prompt available",
                        createdAt: generationDate,
                        userId: userId,
                        generationId: doc.id,
                      },
                    ],
                  });
                }
              }
            }
          }
        }
      });

      // Calculate user engagement levels based on custom model generations
      let powerUsers = 0;
      let regularUsers = 0;
      let casualUsers = 0;
      let inactiveUsers = totalUsers - customModelGenerationUsers.size;

      customModelGenerationUsers.forEach((userId) => {
        const count = userGenerationCounts.get(userId) || 0;
        if (count >= 10) {
          powerUsers++;
        } else if (count >= 3) {
          regularUsers++;
        } else if (count >= 1) {
          casualUsers++;
        }
      });

      // Calculate time-to-action metrics
      let totalTimeToFirstTraining = 0;
      let totalTimeToCustomModel = 0;
      let totalTimeToFirstGeneration = 0;
      let totalTimeToSubscription = 0;
      let trainingCount_time = 0;
      let firstGenCount = 0;
      let customModelCount_time = 0;
      let subscriptionCount = 0;

      usersWithMetadata.forEach((meta) => {
        if (meta.firstTrainingAt) {
          const timeDiff =
            (meta.firstTrainingAt.getTime() - meta.createdAt.getTime()) /
            (1000 * 60 * 60 * 24);
          totalTimeToFirstTraining += timeDiff;
          trainingCount_time++;
        }
        if (meta.firstCustomModelAt) {
          const timeDiff =
            (meta.firstCustomModelAt.getTime() - meta.createdAt.getTime()) /
            (1000 * 60 * 60 * 24);
          totalTimeToCustomModel += timeDiff;
          customModelCount_time++;
        }
        if (meta.firstGenerationAt) {
          const timeDiff =
            (meta.firstGenerationAt.getTime() - meta.createdAt.getTime()) /
            (1000 * 60 * 60 * 24);
          totalTimeToFirstGeneration += timeDiff;
          firstGenCount++;
        }
        if (meta.subscriptionDate) {
          const timeDiff =
            (meta.subscriptionDate.getTime() - meta.createdAt.getTime()) /
            (1000 * 60 * 60 * 24);
          totalTimeToSubscription += timeDiff;
          subscriptionCount++;
        }
      });

      // Calculate user model usage categories
      let baseModelOnlyUsers = 0;
      let customModelOnlyUsers = 0;
      let bothModelUsers = 0;
      let totalActiveUsers = 0;

      userModelMap.forEach((stats) => {
        totalActiveUsers++;
        if (stats.usedBaseModel && stats.usedCustomModel) {
          bothModelUsers++;
        } else if (stats.usedBaseModel) {
          baseModelOnlyUsers++;
        } else if (stats.usedCustomModel) {
          customModelOnlyUsers++;
        }
      });

      // Inactive users are total users minus those who created generations
      const inactiveUsersInPeriod = totalUsers - totalActiveUsers;

      // Count subscribed users
      const subscribedUsers = Array.from(usersWithMetadata.values()).filter(
        (meta) => meta.hasSubscription
      ).length;

      // Update all state
      setGenerationCount(filteredGenerationsCount);
      setFeedbackStats({
        positive: positiveCount,
        negative: negativeCount,
      });
      setModelUsage({
        baseModel: baseModelCount,
        customModels: customModelCount,
      });
      setUserModelUsage({
        baseModelOnly: baseModelOnlyUsers,
        customModelsOnly: customModelOnlyUsers,
        bothModels: bothModelUsers,
        inactive: inactiveUsersInPeriod,
      });

      // Set conversion funnel metrics
      setConversionFunnel({
        totalSignups: totalUsers,
        trainingUsers: trainingUsers.size,
        modelUsers: customModelUsers.size,
        generationUsers: customModelGenerationUsers.size,
        subscribedUsers,
      });

      // Set user engagement metrics
      setUserEngagement({
        powerUsers,
        regularUsers,
        casualUsers,
        inactiveUsers,
      });

      // Set time-to-action metrics
      setTimeToAction({
        avgTimeToFirstTraining:
          trainingCount_time > 0
            ? totalTimeToFirstTraining / trainingCount_time
            : 0,
        avgTimeToCustomModel:
          customModelCount_time > 0
            ? totalTimeToCustomModel / customModelCount_time
            : 0,
        avgTimeToFirstGeneration:
          firstGenCount > 0 ? totalTimeToFirstGeneration / firstGenCount : 0,
        avgTimeToSubscription:
          subscriptionCount > 0
            ? totalTimeToSubscription / subscriptionCount
            : 0,
      });

      // Sort reasons by count and get the top 5
      const sortedReasons = Array.from(reasonsMap.entries())
        .map(([reason, { count, generations }]) => ({
          reason,
          count,
          generations: generations.slice(0, 5),
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setTopReasons(sortedReasons);
    } catch (error) {
      console.error("Error fetching metrics:", error);
    } finally {
      setLoading(false);
    }
  }, [db, getDateRange]);

  // Effect to fetch metrics on component mount and when date range changes
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Handle time period change
  const handleTimePeriodChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setSelectedTimePeriod(newValue);
  };

  // Format numbers for display
  const formatNumber = (num: number | null): string => {
    if (num === null) return "-";

    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Calculate feedback ratio
  const calculateFeedbackRatio = (): number => {
    if (!feedbackStats) return 0;
    const total = feedbackStats.positive + feedbackStats.negative;
    return total > 0 ? (feedbackStats.positive / total) * 100 : 0;
  };

  // Calculate base model vs custom model usage percentages
  const calculateModelUsagePercentage = (
    type: "baseModel" | "customModels"
  ): number => {
    const { baseModel, customModels } = modelUsage;
    const total = baseModel + customModels;
    if (total === 0) return 0;
    return type === "baseModel"
      ? (baseModel / total) * 100
      : (customModels / total) * 100;
  };

  // Calculate user model usage percentages
  const calculateUserModelPercentage = (
    type: "baseModelOnly" | "customModelsOnly" | "bothModels" | "inactive"
  ): number => {
    const { baseModelOnly, customModelsOnly, bothModels, inactive } =
      userModelUsage;
    const total = baseModelOnly + customModelsOnly + bothModels + inactive;
    if (total === 0) return 0;

    switch (type) {
      case "baseModelOnly":
        return (baseModelOnly / total) * 100;
      case "customModelsOnly":
        return (customModelsOnly / total) * 100;
      case "bothModels":
        return (bothModels / total) * 100;
      case "inactive":
        return (inactive / total) * 100;
      default:
        return 0;
    }
  };

  // Calculate conversion funnel rates
  const calculateConversionRate = (
    from:
      | "signups"
      | "trainingUsers"
      | "modelUsers"
      | "generationUsers"
      | "subscribedUsers",
    to: "trainingUsers" | "modelUsers" | "generationUsers" | "subscribedUsers"
  ): number => {
    const {
      totalSignups,
      trainingUsers,
      modelUsers,
      generationUsers,
      subscribedUsers,
    } = conversionFunnel;

    let fromCount = 0;
    let toCount = 0;

    switch (from) {
      case "signups":
        fromCount = totalSignups;
        break;
      case "trainingUsers":
        fromCount = trainingUsers;
        break;
      case "modelUsers":
        fromCount = modelUsers;
        break;
      case "generationUsers":
        fromCount = generationUsers;
        break;
      case "subscribedUsers":
        fromCount = subscribedUsers;
        break;
    }

    switch (to) {
      case "trainingUsers":
        toCount = trainingUsers;
        break;
      case "modelUsers":
        toCount = modelUsers;
        break;
      case "generationUsers":
        toCount = generationUsers;
        break;
      case "subscribedUsers":
        toCount = subscribedUsers;
        break;
    }

    return fromCount > 0 ? (toCount / fromCount) * 100 : 0;
  };

  // Calculate engagement percentages
  const calculateEngagementPercentage = (
    type: "power" | "regular" | "casual" | "inactive"
  ): number => {
    const { powerUsers, regularUsers, casualUsers, inactiveUsers } =
      userEngagement;
    const total = powerUsers + regularUsers + casualUsers + inactiveUsers;
    if (total === 0) return 0;

    switch (type) {
      case "power":
        return (powerUsers / total) * 100;
      case "regular":
        return (regularUsers / total) * 100;
      case "casual":
        return (casualUsers / total) * 100;
      case "inactive":
        return (inactiveUsers / total) * 100;
      default:
        return 0;
    }
  };

  // Format time in days
  const formatDays = (days: number): string => {
    if (days === 0) return "N/A";
    if (days < 1) return `${Math.round(days * 24)}h`;
    if (days < 7) return `${Math.round(days * 10) / 10}d`;
    return `${Math.round(days)}d`;
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#0B0B0F] text-white p-2 md:px-32 md:py-8 font-custom flex justify-center items-center">
        <CircularProgress color="inherit" size={40} />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-screen-xl mx-auto p-2 md:px-16 md:py-8">
        <div className="mb-6 border-b border-[#282828] pb-4">
          <Typography
            variant="h6"
            fontWeight="bold"
            className="mb-2"
            color="white"
          >
            HQ Dashboard
          </Typography>
          <div className="flex justify-between items-center">
            <Typography variant="body2" color="white">
              Analytics and insights for ThumbGenie
            </Typography>
            <Tabs
              value={selectedTimePeriod}
              onChange={handleTimePeriodChange}
              sx={{
                minHeight: "32px",
                "& .MuiTabs-indicator": { backgroundColor: "#1DB954" },
                "& .MuiTab-root": { minHeight: "32px", padding: "0 16px" },
              }}
            >
              {TIME_PERIODS.map((period, index) => (
                <Tab
                  key={index}
                  label={period}
                  sx={{
                    color: "white",
                    fontSize: "12px",
                    "&.Mui-selected": { color: "#1DB954" },
                  }}
                />
              ))}
            </Tabs>
          </div>
        </div>

        <Grid container spacing={2} className="mb-4">
          <Grid item xs={12} sm={6} md={2.4}>
            <div className="text-center p-4 bg-[#1E1E1E] rounded-lg border border-[#2A2A2A]">
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#60A5FA"
                className="mb-1"
              >
                {formatNumber(userCount)}
              </Typography>
              <Typography
                fontSize={13}
                color="rgba(255, 255, 255, 0.6)"
                className="mb-1"
              >
                Total Users
              </Typography>
              <Typography fontSize={11} color="rgba(255, 255, 255, 0.4)">
                Your total user base
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <div className="text-center p-4 bg-[#1E1E1E] rounded-lg border border-[#2A2A2A]">
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#10B981"
                className="mb-1"
              >
                {calculateConversionRate("signups", "trainingUsers").toFixed(1)}
                %
              </Typography>
              <Typography
                fontSize={13}
                color="rgba(255, 255, 255, 0.6)"
                className="mb-1"
              >
                Training Rate
              </Typography>
              <Typography fontSize={11} color="#10B981">
                Users who create 1+ trainings
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <div className="text-center p-4 bg-[#1E1E1E] rounded-lg border border-[#2A2A2A]">
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#F59E0B"
                className="mb-1"
              >
                {calculateConversionRate("trainingUsers", "modelUsers").toFixed(
                  1
                )}
                %
              </Typography>
              <Typography
                fontSize={13}
                color="rgba(255, 255, 255, 0.6)"
                className="mb-1"
              >
                Model Adoption
              </Typography>
              <Typography fontSize={11} color="#F59E0B">
                Trained → Custom Models
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <div className="text-center p-4 bg-[#1E1E1E] rounded-lg border border-[#2A2A2A]">
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#8B5CF6"
                className="mb-1"
              >
                {calculateConversionRate(
                  "modelUsers",
                  "generationUsers"
                ).toFixed(1)}
                %
              </Typography>
              <Typography
                fontSize={13}
                color="rgba(255, 255, 255, 0.6)"
                className="mb-1"
              >
                Generation Adoption
              </Typography>
              <Typography fontSize={11} color="#8B5CF6">
                Models → Active Usage
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <div className="text-center p-4 bg-[#1E1E1E] rounded-lg border border-[#2A2A2A]">
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#EF4444"
                className="mb-1"
              >
                {calculateConversionRate("signups", "subscribedUsers").toFixed(
                  2
                )}
                %
              </Typography>
              <Typography
                fontSize={13}
                color="rgba(255, 255, 255, 0.6)"
                className="mb-1"
              >
                Subscription Rate
              </Typography>
              <Typography fontSize={11} color="#EF4444">
                Signups → Paid (Target: 2-5%)
              </Typography>
            </div>
          </Grid>
        </Grid>

        {/* Conversion Funnel Analysis */}
        <Grid container spacing={2} className="mb-4">
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: "#1E1E1E", color: "white" }}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  marginBottom={2}
                  className="flex items-center"
                >
                  <IconChartPie size={20} className="mr-2 text-orange-500" />
                  Conversion Funnel Breakdown
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <div className="text-center p-3 bg-[#2A2A2A] rounded-lg">
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="#60A5FA"
                      >
                        {formatNumber(conversionFunnel.totalSignups)}
                      </Typography>
                      <Typography
                        fontSize={12}
                        color="rgba(255, 255, 255, 0.6)"
                      >
                        Signup Users
                      </Typography>
                      <Typography
                        fontSize={11}
                        color="rgba(255, 255, 255, 0.4)"
                      >
                        100% - Starting point
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <div className="text-center p-3 bg-[#2A2A2A] rounded-lg">
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="#34D399"
                      >
                        {formatNumber(conversionFunnel.trainingUsers)}
                      </Typography>
                      <Typography
                        fontSize={12}
                        color="rgba(255, 255, 255, 0.6)"
                      >
                        Trained Users
                      </Typography>
                      <Typography fontSize={11} color="#34D399">
                        {calculateConversionRate(
                          "signups",
                          "trainingUsers"
                        ).toFixed(1)}
                        % of signups
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <div className="text-center p-3 bg-[#2A2A2A] rounded-lg">
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="#F59E0B"
                      >
                        {formatNumber(conversionFunnel.modelUsers)}
                      </Typography>
                      <Typography
                        fontSize={12}
                        color="rgba(255, 255, 255, 0.6)"
                      >
                        Trained Model Users
                      </Typography>
                      <Typography fontSize={11} color="#F59E0B">
                        {calculateConversionRate(
                          "trainingUsers",
                          "modelUsers"
                        ).toFixed(1)}
                        % create models
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <div className="text-center p-3 bg-[#2A2A2A] rounded-lg">
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="#10B981"
                      >
                        {formatNumber(conversionFunnel.generationUsers)}
                      </Typography>
                      <Typography
                        fontSize={12}
                        color="rgba(255, 255, 255, 0.6)"
                      >
                        Trained Generation Users
                      </Typography>
                      <Typography fontSize={11} color="#10B981">
                        {calculateConversionRate(
                          "modelUsers",
                          "generationUsers"
                        ).toFixed(1)}
                        % use models
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <div className="text-center p-3 bg-[#2A2A2A] rounded-lg">
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="#EF4444"
                      >
                        {formatNumber(conversionFunnel.subscribedUsers)}
                      </Typography>
                      <Typography
                        fontSize={12}
                        color="rgba(255, 255, 255, 0.6)"
                      >
                        Paying Users
                      </Typography>
                      <Typography fontSize={11} color="#EF4444">
                        {calculateConversionRate(
                          "signups",
                          "subscribedUsers"
                        ).toFixed(2)}
                        % final conversion
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Group feedback metrics */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "#1E1E1E", color: "white" }}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  marginBottom={1}
                >
                  Feedback Stats
                </Typography>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Typography color="rgba(255, 255, 255, 0.6)" fontSize={13}>
                      Positive Ratings:
                    </Typography>
                    <Typography
                      fontWeight="medium"
                      className="flex items-center"
                      fontSize={13}
                    >
                      <IconThumbUp size={14} className="text-green-500 mr-1" />
                      {formatNumber(feedbackStats?.positive || 0)}
                    </Typography>
                  </div>
                  <div className="flex justify-between">
                    <Typography color="rgba(255, 255, 255, 0.6)" fontSize={13}>
                      Negative Ratings:
                    </Typography>
                    <Typography
                      fontWeight="medium"
                      className="flex items-center"
                      fontSize={13}
                    >
                      <IconThumbDown size={14} className="text-red-500 mr-1" />
                      {formatNumber(feedbackStats?.negative || 0)}
                    </Typography>
                  </div>
                  <div className="flex justify-between">
                    <Typography color="rgba(255, 255, 255, 0.6)" fontSize={13}>
                      Feedback Ratio:
                    </Typography>
                    <Typography
                      fontWeight="medium"
                      className="flex items-center"
                      fontSize={13}
                    >
                      {calculateFeedbackRatio().toFixed(0)}% positive
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "#1E1E1E", color: "white" }}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  marginBottom={1}
                >
                  Top Negative Feedback Reasons
                </Typography>
                {topReasons.length > 0 ? (
                  <div className="space-y-2">
                    {topReasons.map((reason, index) => (
                      <Accordion
                        key={index}
                        sx={{
                          backgroundColor: "#23232A",
                          color: "white",
                          borderRadius: 2,
                          boxShadow: "none",
                          mb: 1,
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <IconChevronDown size={14} className="text-white" />
                          }
                          aria-controls={`panel${index}-content`}
                          id={`panel${index}-header`}
                          sx={{
                            minHeight: 0,
                            "& .MuiAccordionSummary-content": {
                              margin: 0,
                              alignItems: "center",
                            },
                          }}
                        >
                          <div className="flex justify-between items-center w-full">
                            <Typography
                              color="rgba(255, 255, 255, 0.8)"
                              fontSize={13}
                              className="flex items-center"
                            >
                              <IconX size={10} className="text-red-500 mr-2" />
                              {reason.reason}
                            </Typography>
                            <Typography fontWeight="medium" fontSize={13}>
                              {reason.count}
                            </Typography>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            backgroundColor: "#23232A",
                            borderTop: "1px solid #333",
                            p: 2,
                          }}
                        >
                          <div className="space-y-2">
                            {reason.generations.map((gen, genIndex) => (
                              <div
                                key={genIndex}
                                className="text-sm mb-2 flex items-center"
                              >
                                <Typography variant="body2" className="italic">
                                  "{gen.prompt}"
                                </Typography>
                                <a
                                  href={`https://console.firebase.google.com/u/0/project/soc-ia-l/firestore/databases/-default-/data/~2Fusers~2F${gen.userId}~2Fgenerations~2F${gen.generationId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-2 text-blue-400 underline text-xs"
                                  title="View in Firestore"
                                >
                                  View
                                </a>
                                <Typography
                                  variant="caption"
                                  className="text-gray-400 ml-2"
                                >
                                  {moment(gen.createdAt).format("MMM D, YYYY")}
                                </Typography>
                              </div>
                            ))}
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </div>
                ) : (
                  <Typography color="rgba(255, 255, 255, 0.5)" fontSize={13}>
                    No negative feedback with reasons in this period
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default HqScreen;
